import { injectable, inject } from 'inversify';
import type { ICasePersistent, ICaseState } from './CasePersistance/CasePersistenceHub';
import { TYPES } from '@/engine/ioc';
import type { ViewsService } from './Views.service';
import type { SceneItemKind } from './interfaces';
import type { ViewEntity } from './models/viewEntity/view.entity.ts';
import type { IStrategyByKindProvider } from './LoadStateStrategiesProvider';

enum IndicatedSceneItemKind {
  IndicatedAnnotation = 'IndicatedAnnotation',
  IndicatedDistance = 'IndicatedDistance',
  IndicatedAngle = 'IndicatedAngle',
  IndicatedAreaMeasurement = 'IndicatedAreaMeasurement',
  IndicatedSplineMeasurement = 'IndicatedSplineMeasurement',
  IndicatedSpline = 'IndicatedSpline'
}

interface viewData {
  viewDescriptor: string;
  indications: Array<IndicationData>;
}

export interface IndicationData {
  descriptor: string;
  kind: SceneItemKind;
  phaseId: string | undefined;
  attachments: Array<string>;
  label: string;
  isVisible?: boolean;
  details?: unknown;
}

export type StateMetadata = Array<viewData>;

@injectable()
export class IndicationsResolver implements ICasePersistent {
  private readonly _viewsService: ViewsService;

  private readonly _strategyByKindProvider: IStrategyByKindProvider;

  public constructor(
    @inject(TYPES.ViewsService) viewsService: ViewsService,
    @inject(TYPES.IStrategyByKindProvider) strategyByKindProvider: IStrategyByKindProvider
  ) {
    this._viewsService = viewsService;
    this._strategyByKindProvider = strategyByKindProvider;
  }

  public saveState(): Promise<ICaseState> {
    const views = this._viewsService.views;
    const dataToSave: StateMetadata = [];
    const indicatedKinds = Object.values<string>(IndicatedSceneItemKind);

    views.forEach((view) => {
      const viewDataToSave: viewData = {
        viewDescriptor: view.getDescriptor(),
        indications: []
      };

      const viewItems = [...view.getObjects(), ...view.getMeasurements()];

      const indicatedObjects = viewItems.filter((object) => {
        return indicatedKinds.includes(object.kind) && !object.imported;
      });
      indicatedObjects?.forEach((object) => {
        viewDataToSave.indications.push({
          descriptor: object.descriptor,
          kind: object.kind,
          phaseId: object.phaseId,
          attachments: object.attachments,
          label: object.label,
          isVisible: object.isVisible,
        });
      });
      dataToSave.push(viewDataToSave);
    });

    const state: ICaseState<StateMetadata> = {
      version: '1.0',
      metadata: dataToSave
    };

    return Promise.resolve(state);
  }

  public loadState(metadata: unknown): Promise<void> {
    const viewsIndicationsData = metadata as StateMetadata;

    viewsIndicationsData.forEach((viewData) => {
      const view = this._viewsService.getViewByDescriptor(viewData.viewDescriptor);
      if (!view) return;

      viewData.indications.forEach((indicationData: IndicationData) => {
        const loadStrategy = this._strategyByKindProvider.getStrategy(indicationData.kind);
        loadStrategy.loadState(view, indicationData);
      });
    });
    return Promise.resolve();
  }
}

export interface ILoadStateStrategy {
  loadState: (view: ViewEntity, indicationData: IndicationData) => void;
}
